import React, { ReactElement, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import Button from '../../../buttons/Button';
import { Pick } from '../../../../types';
import { calculateAmountToPick, stripLeadingZeroes, mergeClassNames } from '../../../../utils';

import styles from './CommercialSkippedItemReportView.css';

type Props = {
  picks: Pick[];
  forwardingStore: string | null;
  onComplete: () => void;
  onEdit: () => void;
  editOrderEnabled: boolean;
  isSmartSourcingEnabled: boolean;
};

const CommercialSkippedItemReportView = ({
  picks,
  forwardingStore,
  onComplete,
  onEdit,
  editOrderEnabled,
  isSmartSourcingEnabled,
}: Props): ReactElement => {
  const { t } = useTranslation();
  const translationRoot = 'COMMERCIAL_SKIP_FLOW';

  const skippedPicks = useMemo(() => picks.filter(pick => pick.isSkipped), [picks]);

  const getSkipFlowTitleString = (): string => {
    if (!isSmartSourcingEnabled) {
      return 'ALTERNATIVE_SOURCE_REQUIRED';
    }
    if (forwardingStore) {
      return 'TRANSFER_STORE';
    }
    return 'VDP_OR_OUTSIDE';
  };

  const getSkipFlowContentString = (): string => {
    if (!isSmartSourcingEnabled) {
      return t(`${translationRoot}.SKIPPED_ITEMS_NO_SMART_SOURCING`);
    }
    if (forwardingStore) {
      return `${t(`${translationRoot}.SKIPPED_ITEMS_AVAILABLE`)} ${stripLeadingZeroes(forwardingStore)}.`;
    }
    return t(`${translationRoot}.SKIPPED_ITEMS_NOT_AVAILABLE`);
  };

  const getButtonTextKey = (): string => {
    return forwardingStore ? 'MANUAL' : 'CONFIRM';
  };

  const skipFlowContent = getSkipFlowContentString();
  const skipFlowTitle = getSkipFlowTitleString();
  const buttonText = getButtonTextKey();

  // Render enhanced UI when feature flag is enabled
  if (editOrderEnabled) {
    return (
      <section className={styles.section}>
        <header className={styles.header}>
          <h3 className={styles.headerText}>{t(`${translationRoot}.SKIPPED_ITEM_REPORT_TITLE`)}</h3>
          <article className={styles.article}>
            <p className={styles.p}>{t(`${translationRoot}.SKIPPED_ITEM_REPORT_MESSAGE`)}</p>
          </article>

          <div className={styles.gridContainer} role="table" aria-label="Skipped items report">
            <div className={styles.gridHeader} role="row">
              <div className={styles.headerCell} role="columnheader">
                {t(`${translationRoot}.CUSTOMER_NAME`)}
              </div>
              <div className={styles.headerCell} role="columnheader">
                {t('ACTIVE_PICK_TOUR.DETAILS_VIEW.PART_NUMBER')}
              </div>
              <div className={styles.headerCell} role="columnheader">
                {t(`${translationRoot}.PART_DESCRIPTION`)}
              </div>
              <div className={styles.headerCell} role="columnheader">
                {t('ACTIVE_PICK_TOUR.PICK_TOUR_FOOTER.QTY')}
              </div>
            </div>

            {skippedPicks.map(pick => (
              <div key={`${pick.orderID}-${pick.lineNumber}`} className={styles.gridRow} role="row">
                <div className={styles.gridCell} data-label={t(`${translationRoot}.CUSTOMER_NAME`)} role="cell">
                  {pick.destination}
                </div>
                <div
                  className={styles.gridCell}
                  data-label={t('ACTIVE_PICK_TOUR.DETAILS_VIEW.PART_NUMBER')}
                  role="cell"
                >
                  {pick.partNumber}
                </div>
                <div className={styles.gridCell} data-label={t(`${translationRoot}.PART_DESCRIPTION`)} role="cell">
                  {pick.description}
                </div>
                <div className={styles.gridCell} data-label={t('ACTIVE_PICK_TOUR.PICK_TOUR_FOOTER.QTY')} role="cell">
                  {calculateAmountToPick(pick) - pick.pickedQuantity}
                </div>
              </div>
            ))}
          </div>
        </header>

        <footer className={styles.footer}>
          <Button className={styles.button} color="secondary" onClick={onEdit}>
            {t(`${translationRoot}.BUTTONS.EDIT_ORDER`)}
          </Button>
          <Button className={styles.button} color="primary" onClick={onComplete}>
            {t(`${translationRoot}.BUTTONS.ACKNOWLEDGE_AND_FINALIZE`)}
          </Button>
        </footer>
      </section>
    );
  }

  // Render standard UI when feature flag is disabled
  return (
    <section className={styles.section}>
      <header className={styles.header}>
        <h3 className={mergeClassNames(styles.headerText, !isSmartSourcingEnabled ? styles.noSmartSourcingHeader : '')}>
          {t(`${translationRoot}.${skipFlowTitle}`)}
        </h3>
        <article className={styles.article}>
          <p className={styles.p}>{skipFlowContent}</p>
        </article>
        <ul className={styles.list}>
          <li className={styles.listHeader}>
            <h5 className={styles.partLabel}>{t('ACTIVE_PICK_TOUR.DETAILS_VIEW.PART_NUMBER')}</h5>
            <h5 className={styles.qtyLabel}>{t('ACTIVE_PICK_TOUR.PICK_TOUR_FOOTER.QTY')}</h5>
          </li>
          {skippedPicks.map(pick => (
            <li key={`${pick.orderID}-${pick.lineNumber}`} className={styles.listItem}>
              <span className={styles.part}>{pick.partNumber}</span>
              <span className={styles.qty}>{calculateAmountToPick(pick) - pick.pickedQuantity}</span>
            </li>
          ))}
        </ul>
      </header>
      <footer className={styles.footer}>
        <Button className={styles.button} color="secondary" onClick={onComplete}>
          {t(`${translationRoot}.BUTTONS.${buttonText}`)}
        </Button>
      </footer>
    </section>
  );
};

export default CommercialSkippedItemReportView;
